/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"

import type { ApkDetail } from "../../features/apk-detail/apk-detail-types"
import { externalLinks } from "../../features/apk-detail/apk-links"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"
import { ExternalLink } from "./external-link"

interface Props {
    /**
     * APK identifiers used to build the external links.
     *
     * Stores search by package name; Koodous also uses the APK hash, and
     * VirusTotal uses the hash alone.
     */
    apk: Pick<ApkDetail, "packageName" | "apkSignature">
}

/**
 * Links to external stores and malware databases for the APK.
 *
 * @example
 * <ApkExternalLinks apk={apk} />
 */
export const ApkExternalLinks = observer(({ apk }: Props) => (
    <Card>
        <CardHeader>
            <CardTitle>
                <h3 className="m-0 text-xl font-bold uppercase">External Sources</h3>
            </CardTitle>
        </CardHeader>
        <CardContent>
            {/* Preserve list semantics in Safari when the list has no default styling. */}
            <ul role="list" className="m-0 flex list-none flex-col items-start gap-2 p-0">
                {externalLinks(apk).map(({ label, href }) => (
                    <li key={label}>
                        <ExternalLink href={href}>{label}</ExternalLink>
                    </li>
                ))}
            </ul>
        </CardContent>
    </Card>
))
